import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShieldOff, Loader2, AlertTriangle, RefreshCw, User } from 'lucide-react';
import { toast } from 'sonner';

export default function PrivacyScrubPage() {
  const [searchParams] = useSearchParams();
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerId, setCustomerId] = useState(searchParams.get('customer_id') || '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [scrubbing, setScrubbing] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/privacy/audit-log');
      setAuditLog(r.data || []);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openConfirm = () => {
    if (!customerId.trim()) {
      toast.error('Enter a customer ID');
      return;
    }
    setConfirmText('');
    setResult(null);
    setConfirmOpen(true);
  };

  const runScrub = async () => {
    if (confirmText !== 'SCRUB') return;
    setScrubbing(true);
    try {
      const r = await api.post(`/admin/privacy/scrub-customer/${customerId.trim()}`);
      setResult(r.data);
      toast.success('Customer scrubbed successfully');
      setConfirmOpen(false);
      setCustomerId('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scrub failed');
    } finally { setScrubbing(false); }
  };

  return (
    <div data-testid="privacy-scrub-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldOff className="h-4 w-4 text-ink" />
          <h1 className="text-base font-semibold text-ink">Privacy Scrub</h1>
        </div>
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={load}>
          <RefreshCw className="h-3 w-3" /> Refresh Log
        </Button>
      </div>

      {/* Scrub trigger card */}
      <Card className="bg-white border-stone/50 mb-5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="rounded-full h-8 w-8 flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(185,28,28,0.08)' }}>
              <AlertTriangle className="h-4 w-4" style={{ color: '#B91C1C' }} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-ink">Anonymize a customer</h3>
              <p className="text-[11px] text-muted-foreground">
                Irreversibly scrubs PII (email, name, phone, address, magic links, email deliveries, event metadata) while preserving order and document IDs for referential integrity. An audit event is recorded.
              </p>
            </div>
          </div>
          <Separator className="my-3" />
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Customer ID (UUID)</Label>
              <Input
                value={customerId}
                onChange={e => setCustomerId(e.target.value)}
                placeholder="e.g. 3f2c04a0-..."
                className="mt-1 h-8 text-xs font-mono"
                data-testid="scrub-customer-id-input"
              />
            </div>
            <Button
              size="sm"
              variant="destructive"
              className="h-8 text-xs gap-1"
              onClick={openConfirm}
              disabled={!customerId.trim()}
              data-testid="scrub-open-confirm-btn"
            >
              <ShieldOff className="h-3 w-3" /> Scrub
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            Tip: Look up a user in the Users page to grab their ID. The Scrub button on the User Detail page routes here pre-filled.
          </p>
        </CardContent>
      </Card>

      {/* Result summary after successful scrub */}
      {result && (
        <Card className="bg-emerald-50/50 border-emerald-200/50 mb-5" data-testid="scrub-result-card">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-emerald-800 mb-2">Scrub complete</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <div>Anonymized email: <span className="font-mono">{result.anonymized_email}</span></div>
              <div>Addresses: <strong>{result.addresses_scrubbed}</strong></div>
              <div>Events: <strong>{result.events_scrubbed}</strong></div>
              <div>Email deliveries: <strong>{result.email_deliveries_scrubbed}</strong></div>
              <div>Provider events: <strong>{result.email_provider_events_scrubbed}</strong></div>
              <div>Audit ID: <span className="font-mono text-[10px]">{result.audit_event_id}</span></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit log */}
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Privacy Audit Log</h3>
        <Badge variant="outline" className="text-[10px]">{auditLog.length}</Badge>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : auditLog.length === 0 ? (
        <Card className="bg-white border-stone/50"><CardContent className="p-6 text-center"><p className="text-sm text-muted-foreground">No privacy actions recorded yet.</p></CardContent></Card>
      ) : (
        <div className="space-y-1" data-testid="privacy-audit-list">
          {auditLog.map((e, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-stone/50 bg-white text-xs">
              <div className="rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(107,100,92,0.08)' }}>
                <User className="h-3 w-3 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[9px]">{e.action}</Badge>
                  <span className="font-mono text-[10px] text-muted-foreground truncate">{e.entity_id}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  by {e.actor_email || e.actor_id} — {e.metadata?.original_email_prefix || ''}
                  {e.metadata?.anonymized_email ? ` → ${e.metadata.anonymized_email}` : ''}
                </p>
              </div>
              <span className="text-[10px] text-muted-foreground flex-shrink-0">
                {e.created_at ? new Date(e.created_at).toLocaleString() : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm text-red-700">Confirm Privacy Scrub</DialogTitle></DialogHeader>
          <div className="space-y-3 text-xs">
            <div className="flex items-start gap-2 p-3 rounded bg-red-50 border border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">This is irreversible.</p>
                <p className="text-red-700 mt-1">
                  Customer <span className="font-mono">{customerId}</span> will be fully anonymized. Their orders and documents remain, but every trace of PII will be replaced.
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Type <strong>SCRUB</strong> to confirm</Label>
              <Input
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                className="mt-1 h-8 text-sm font-mono"
                autoFocus
                data-testid="scrub-confirm-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={runScrub}
                disabled={confirmText !== 'SCRUB' || scrubbing}
                data-testid="scrub-confirm-btn"
              >
                {scrubbing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Scrub Customer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
